/**
 * Notebook MCP Command Handlers (renderer-side)
 *
 * Handles synchronous notebook cell operations via workspace store.
 * Async operations (run, save, restart) are queued via notebookMcpStore.
 */

import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { isNotebookTab } from '../../workspace/core/types/workspaceTypes';
import type { NotebookTabContent } from '../../workspace/notebook/types/notebookTypes';
import { isExecutableCell } from '../../workspace/notebook/types/notebookTypes';
import {
  insertCellAfter,
  appendCell,
  deleteCell,
  moveCellUp,
  moveCellDown,
  updateCellSource,
  convertToMarkdown,
  convertToCode,
  convertToJavaScript,
  getCellIndex,
} from '../../workspace/notebook/utils/cellOperations';
import type {
  UiNotebookListCellsInput,
  UiNotebookListCellsResult,
  UiNotebookGetCellInput,
  UiNotebookGetCellResult,
  UiNotebookAddCellInput,
  UiNotebookAddCellResult,
  UiNotebookSetCellSourceInput,
  UiNotebookSetCellSourceResult,
  UiNotebookDeleteCellInput,
  UiNotebookDeleteCellResult,
  UiNotebookMoveCellInput,
  UiNotebookMoveCellResult,
  UiNotebookConvertCellInput,
  UiNotebookConvertCellResult,
  UiNotebookClearOutputsInput,
  UiNotebookClearOutputsResult,
} from '../../../types/mcpUiControl';

/**
 * Normalize escaped newlines/tabs in source strings from AI tool calls.
 * AI models often send literal "\n" (two chars) instead of actual newlines in JSON.
 */
function normalizeSource(source: string): string {
  return source.replace(/\\n/g, '\n').replace(/\\t/g, '\t');
}

function getNotebookContent(tabId: string): { content: NotebookTabContent } | { error: string } {
  const { tabs } = useWorkspaceStore.getState();
  const tab = tabs.find((t) => t.id === tabId);
  if (!tab) return { error: `Tab not found: ${tabId}` };
  if (!isNotebookTab(tab)) return { error: 'Not a notebook tab' };
  return { content: tab.content as NotebookTabContent };
}

export const handleNotebookListCells = (input: UiNotebookListCellsInput): UiNotebookListCellsResult => {
  const result = getNotebookContent(input.tabId);
  if ('error' in result) return { success: false, error: result.error };
  return {
    success: true,
    cells: result.content.cells.map((cell) => ({
      id: cell.id,
      cellType: cell.cellType,
      sourcePreview: cell.source.slice(0, 200),
      executionState: isExecutableCell(cell) ? cell.executionState : undefined,
      executionOrder: isExecutableCell(cell) ? cell.executionOrder : undefined,
    })),
  };
};

export const handleNotebookGetCell = (input: UiNotebookGetCellInput): UiNotebookGetCellResult => {
  const result = getNotebookContent(input.tabId);
  if ('error' in result) return { success: false, error: result.error };
  const cell = result.content.cells.find((c) => c.id === input.cellId);
  if (!cell) return { success: false, error: `Cell not found: ${input.cellId}` };
  const execCell = isExecutableCell(cell) ? cell : undefined;
  return {
    success: true,
    id: cell.id,
    cellType: cell.cellType,
    source: cell.source,
    executionState: execCell?.executionState,
    executionOrder: execCell?.executionOrder,
    outputs: execCell?.outputs ? {
      resultSetCount: execCell.outputs.resultSets.length,
      messageCount: execCell.outputs.messages.length,
      durationMs: execCell.outputs.durationMs,
      totalRowCount: execCell.outputs.totalRowCount,
      columns: execCell.outputs.resultSets[0]?.columns?.map((c) => ({
        name: c.name ?? '',
        // The row key columns are actually indexed by — equal to `name`
        // except where two columns share a name. `notebook_set_chart_config`
        // requires this, not `name`, so the docs point agents at it.
        key: c.id ?? c.name ?? '',
        type: c.dataType ?? '',
      })),
    } : undefined,
    chartConfig: execCell?.chartConfig,
  };
};

export const handleNotebookAddCell = (input: UiNotebookAddCellInput): UiNotebookAddCellResult => {
  const result = getNotebookContent(input.tabId);
  if ('error' in result) return { success: false, error: result.error };

  let newCellId: string;
  let newCells: typeof result.content.cells;

  if (input.position?.startsWith('after:')) {
    const afterId = input.position.slice(6);
    const inserted = insertCellAfter(result.content.cells, afterId, input.cellType);
    newCells = inserted.cells;
    newCellId = inserted.newCellId;
  } else {
    const appended = appendCell(result.content.cells, input.cellType);
    newCells = appended.cells;
    newCellId = appended.newCellId;
  }

  // Set source if provided (insertCellAfter/appendCell create cells with empty source)
  if (input.source && newCellId) {
    newCells = updateCellSource(newCells, newCellId, normalizeSource(input.source));
  }

  useWorkspaceStore.getState().updateTabContent(input.tabId, { cells: newCells, isDirty: true });
  return { success: true, cellId: newCellId };
};

export const handleNotebookSetCellSource = (input: UiNotebookSetCellSourceInput): UiNotebookSetCellSourceResult => {
  const result = getNotebookContent(input.tabId);
  if ('error' in result) return { success: false, error: result.error };
  const cell = result.content.cells.find((c) => c.id === input.cellId);
  if (!cell) return { success: false, error: `Cell not found: ${input.cellId}` };
  const cells = updateCellSource(result.content.cells, input.cellId, normalizeSource(input.source));
  useWorkspaceStore.getState().updateTabContent(input.tabId, { cells, isDirty: true });
  return { success: true };
};

export const handleNotebookDeleteCell = (input: UiNotebookDeleteCellInput): UiNotebookDeleteCellResult => {
  const result = getNotebookContent(input.tabId);
  if ('error' in result) return { success: false, error: result.error };
  if (result.content.cells.length <= 1) return { success: false, error: 'Cannot delete the last cell' };
  const cell = result.content.cells.find((c) => c.id === input.cellId);
  if (!cell) return { success: false, error: `Cell not found: ${input.cellId}` };
  const cells = deleteCell(result.content.cells, input.cellId);
  useWorkspaceStore.getState().updateTabContent(input.tabId, { cells, isDirty: true });
  return { success: true };
};

export const handleNotebookMoveCell = (input: UiNotebookMoveCellInput): UiNotebookMoveCellResult => {
  const result = getNotebookContent(input.tabId);
  if ('error' in result) return { success: false, error: result.error };
  const idx = getCellIndex(result.content.cells, input.cellId);
  if (idx === -1) return { success: false, error: `Cell not found: ${input.cellId}` };
  const cells = input.direction === 'up'
    ? moveCellUp(result.content.cells, input.cellId)
    : moveCellDown(result.content.cells, input.cellId);
  useWorkspaceStore.getState().updateTabContent(input.tabId, { cells, isDirty: true });
  return { success: true };
};

export const handleNotebookConvertCell = (input: UiNotebookConvertCellInput): UiNotebookConvertCellResult => {
  const result = getNotebookContent(input.tabId);
  if ('error' in result) return { success: false, error: result.error };
  const cell = result.content.cells.find((c) => c.id === input.cellId);
  if (!cell) return { success: false, error: `Cell not found: ${input.cellId}` };
  if (cell.cellType === input.targetType) return { success: true };
  const cells = result.content.cells.map((c) => {
    if (c.id !== input.cellId) return c;
    if (input.targetType === 'markdown') return convertToMarkdown(c);
    if (input.targetType === 'code') return convertToCode(c);
    if (input.targetType === 'javascript') return convertToJavaScript(c);
    return c;
  });
  useWorkspaceStore.getState().updateTabContent(input.tabId, { cells, isDirty: true });
  return { success: true };
};

export const handleNotebookClearOutputs = (input: UiNotebookClearOutputsInput): UiNotebookClearOutputsResult => {
  const result = getNotebookContent(input.tabId);
  if ('error' in result) return { success: false, error: result.error };
  const cells = result.content.cells.map((c) => {
    if (!isExecutableCell(c)) return c;
    if (input.cellId && c.id !== input.cellId) return c;
    return { ...c, outputs: undefined, executionState: 'idle' as const, executionOrder: undefined };
  });
  useWorkspaceStore.getState().updateTabContent(input.tabId, { cells });
  return { success: true };
};
